import type { RidePoint } from "./ride";
import { browserFingerprint } from './browser-crypto';

export interface CandidatePoint extends Omit<RidePoint, "elevationMeters"> {
  readonly elevation: number | undefined;
}

/**
 * Interpolates occasional missing elevation samples without inventing an
 * elevation profile when the source contains no elevation at all.
 *
 * @param candidates - Timed GPS records with optional elevation.
 * @returns Complete ride points.
 */
export function fillMissingElevations(candidates: readonly CandidatePoint[], label = "The FIT activity"): readonly RidePoint[] {
  const knownIndexes = candidates
    .map((point, index) => Number.isFinite(point.elevation) ? index : -1)
    .filter((index) => index >= 0);
  if (knownIndexes.length === 0) {
    throw new Error(`${label} does not contain elevation samples.`);
  }

  const elevations = Array.from<number>({ length: candidates.length });
  const firstKnown = knownIndexes[0]!;
  elevations.fill(candidates[firstKnown]!.elevation!, 0, firstKnown + 1);

  for (let known = 0; known < knownIndexes.length - 1; known += 1) {
    const startIndex = knownIndexes[known]!;
    const endIndex = knownIndexes[known + 1]!;
    const start = candidates[startIndex]!.elevation!;
    const end = candidates[endIndex]!.elevation!;
    const span = endIndex - startIndex;
    for (let index = startIndex; index <= endIndex; index += 1) {
      elevations[index] = start + ((end - start) * (index - startIndex)) / span;
    }
  }

  const lastKnown = knownIndexes.at(-1)!;
  elevations.fill(candidates[lastKnown]!.elevation!, lastKnown);
  return candidates.map((point, index) => ({
    elevationMeters: elevations[index]!,
    latitude: point.latitude,
    longitude: point.longitude,
    recordedAt: point.recordedAt,
  }));
}

/**
 * Converts an untrusted local filename into short React-rendered text.
 *
 * @param fileName - Browser-provided filename.
 * @returns Display title without extension or control characters.
 */
export function titleFromFileName(fileName: string): string {
  const withoutControlCharacters = [...fileName.replace(/\.(fit|gpx|tcx)$/i, "")]
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code >= 32 && code !== 127;
    })
    .join("");
  return withoutControlCharacters
    .replace(/[_-]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim()
    .slice(0, 80);
}

/** Hashes the original bytes without uploading the file. */
export async function fingerprint(content: ArrayBuffer): Promise<string> {
  return browserFingerprint(content);
}

/** Keeps every segment endpoint within a bounded display sample. */
export function sampleForDisplay(points: readonly RidePoint[], limit: number): readonly RidePoint[] {
  if (points.length <= limit) return points;
  if (!points.some(point => point.segmentStart)) return Array.from({ length: limit }, (_, index) =>
    points[Math.round(index * (points.length - 1) / (limit - 1))]!);
  const mandatory = new Set([0, points.length - 1]);
  points.forEach((point, index) => {
    if (point.segmentStart && index > 0) { mandatory.add(index - 1); mandatory.add(index); }
  });
  if (mandatory.size > limit) throw new Error('This trace has too many segments to display.');
  const remaining = limit - mandatory.size;
  const candidates = points.map((_, index) => index).filter(index => !mandatory.has(index));
  for (let index = 0; index < remaining; index += 1) {
    mandatory.add(candidates[Math.floor(index * candidates.length / remaining)]!);
  }
  return [...mandatory].sort((a, b) => a - b).map(index => points[index]!);
}
